import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import ReactQuill from "react-quill";
import {
  IconSend,
  IconPaperclip,
  IconX,
  IconPhoto,
  // ✅ add these
  IconFile,
  IconFileTypePdf,
  IconFileTypeDocx,
  IconFileTypeXls,
  IconFileTypePpt,
  IconFileTypeTxt,
  IconFileTypeCsv,
} from "@tabler/icons-react";
import { useWebSocket } from "../../state/reducers/WebSocketProvider";
import "react-quill/dist/quill.snow.css";
import DOMPurify from "dompurify";

const genId = () => `cid_${Date.now()}_${Math.random().toString(36).slice(2)}`;

export default function AdminComments({ activeTab }) {
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);
  const topRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const processedIds = useRef(new Set());

  const user = useSelector((s) => s.user);
  const selectedStudent = useSelector((s) => s.app.selectedStudent);

  const userId = user?.user_id;
  const username = user?.username;
  const firstName = user?.first_name;
  const lastName = user?.last_name;
  const userRole = user?.role;
  const userPic = user?.profile?.profile_picture_url;

  const studentId = selectedStudent?.student_id || selectedStudent?.id;

  const { createConnection, closeConnection, sendMessage, getConnectionState } =
    useWebSocket();

  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState({});
  const [comment, setComment] = useState("");
  const [loadingHistory, setLoadingHistory] = useState(false);

  // ✅ File upload states
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const roomKey = activeTab?.title?.trim().toLowerCase().replace(/\s+/g, "_");
  const roomname = roomKey && studentId ? `${roomKey}_${studentId}` : null;

  const connectionState = getConnectionState(roomKey, studentId);

  // Generate unique message ID
  const generateMessageId = (msg) => {
    if (msg.message_id) return `mid_${msg.message_id}`;
    if (msg.client_message_id) return `cid_${msg.client_message_id}`;
    return `temp_${msg.sender_id}_${msg.date_sent}`;
  };

  const isUserNearTop = () => {
    const el = messagesContainerRef.current;
    if (!el) return false;
    return el.scrollTop < 100;
  };

  // ✅ FIXED: Handle incoming messages with proper replacement
  const handleIncoming = (msg) => {
    if (!msg) return;

    msg.message = msg.message || msg.content || "";
    msg.sender_id = msg.sender_id || msg.sender;

    // ✅ Priority: If this is a server confirmation (has message_id), replace optimistic
    if (msg.message_id && msg.client_message_id) {
      const uid = `mid_${msg.message_id}`;

      if (processedIds.current.has(uid)) {
        console.log("⚠️ Duplicate server message:", uid);
        return;
      }

      processedIds.current.add(uid);

      console.log("🔄 Server confirmation received:", {
        message_id: msg.message_id,
        client_message_id: msg.client_message_id,
      });

      setPending((p) => {
        const updated = { ...p };
        delete updated[msg.client_message_id];
        return updated;
      });

      setMessages((prev) => {
        const filtered = prev.filter(
          (m) => m.client_message_id !== msg.client_message_id
        );

        return [
          ...filtered,
          { ...msg, isDelivered: true, isPending: false },
        ].sort((a, b) => new Date(b.date_sent) - new Date(a.date_sent));
      });

      return;
    }

    // ✅ Handle messages without client_message_id (from other users)
    const uid = msg.message_id
      ? `mid_${msg.message_id}`
      : `temp_${msg.sender_id}_${msg.date_sent}`;

    if (processedIds.current.has(uid)) {
      console.log("⚠️ Duplicate message:", uid);
      return;
    }

    processedIds.current.add(uid);

    setMessages((prev) =>
      [...prev, { ...msg, isDelivered: true }].sort(
        (a, b) => new Date(b.date_sent) - new Date(a.date_sent)
      )
    );
  };

  const handleMessageFailed = (data) => {
    console.error("❌ Message failed:", data);

    const { client_message_id, error } = data;

    // Remove from pending
    setPending((p) => {
      const updated = { ...p };
      delete updated[client_message_id];
      return updated;
    });

    // Mark message as failed
    setMessages((prev) =>
      prev.map((m) =>
        m.client_message_id === client_message_id
          ? { ...m, isPending: false, isFailed: true, error }
          : m
      )
    );

    alert(`Failed to send message: ${error}`);
  };

  // ✅ Connect WebSocket
  useEffect(() => {
    if (!roomKey || !studentId) {
      console.error("❌ Missing roomKey or studentId");
      return;
    }

    console.log("🔌 Connecting to room:", roomname);

    setMessages([]);
    setPending({});
    processedIds.current.clear();
    setLoadingHistory(true);

    createConnection(roomKey, studentId, {
      userId: userId,
      onMessage: handleIncoming,
      onMessageFailed: handleMessageFailed,
      onHistoryComplete: (data) => {
        console.log("✅ History loaded:", data.count, "messages");
        setLoadingHistory(false);
      },
      onError: (err) => {
        console.error("❌ WebSocket error:", err);
        setLoadingHistory(false);
      },
      onDisconnect: (reason) => {
        console.log("🔴 Disconnected:", reason);
      },
    });

    return () => {
      console.log("🚪 Cleaning up room:", roomname);
      closeConnection(roomKey, studentId);
    };
  }, [roomKey, studentId]);

  // Load draft when roomname or userId changes
  useEffect(() => {
    if (userId && roomname) {
      const savedDraft = localStorage.getItem(`chat_draft_${userId}_${roomname}`) || "";
      setComment(savedDraft);
    } else {
      setComment("");
    }
  }, [roomname, userId]);

  const handleCommentChange = (value) => {
    setComment(value);
    if (userId && roomname) {
      localStorage.setItem(`chat_draft_${userId}_${roomname}`, value);
    }
  };

  // ✅ Auto scroll to top for new messages
  useEffect(() => {
    if (!messagesContainerRef.current) return;

    const lastMsg = messages[0];
    const shouldScroll = isUserNearTop() || lastMsg?.sender_id === userId;

    // if (shouldScroll) {
    //   setTimeout(() => {
    //     topRef.current?.scrollIntoView({ behavior: "smooth" });
    //   }, 100);
    // }
  }, [messages]);

  const allowedExt = new Set([
    "jpg",
    "jpeg",
    "png",
    "gif",
    "webp",
    "pdf",
    "doc",
    "docx",
    "txt",
    "xls",
    "xlsx",
    "csv",
    "ppt",
    "pptx",
  ]);

  // ✅ Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);

    if (files.length === 0) return;
    const validFiles = [];
    const rejected = [];

    for (const file of files) {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      if (allowedExt.has(ext) || file.type.startsWith("image/")) {
        validFiles.push(file);
      } else {
        rejected.push(file.name);
      }
    }

    if (rejected.length) {
      alert(`These files are not supported:\n${rejected.join("\n")}`);
    }

    if (!validFiles.length) return;

    // Create preview objects
    // const filePreviews = files.map((file) => ({
    //   file,
    //   id: genId(),
    //   name: file.name,
    //   size: file.size,
    //   type: file.type,
    //   preview: file.type.startsWith("image/")
    //     ? URL.createObjectURL(file)
    //     : null,
    // }));
    const filePreviews = validFiles.map((file) => ({
      file,
      id: genId(),
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null,
    }));

    setSelectedFiles((prev) => [...prev, ...filePreviews]);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // ✅ Remove file from preview
  const removeFile = (fileId) => {
    setSelectedFiles((prev) => {
      const updated = prev.filter((f) => f.id !== fileId);

      // Cleanup object URLs
      const removed = prev.find((f) => f.id === fileId);
      if (removed?.preview) {
        URL.revokeObjectURL(removed.preview);
      }

      return updated;
    });
  };

  // ✅ Upload files to your backend
  const uploadFiles = async (files) => {
    if (files.length === 0) return [];

    setUploadingFiles(true);

    try {
      const formData = new FormData();

      files.forEach((fileObj) => {
        formData.append("files", fileObj.file);
      });

      // Add metadata
      formData.append("roomname", roomname);
      formData.append("sender_id", userId);

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/chat_app/api/upload-files/`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("File upload failed");
      }

      const data = await response.json();

      console.log("✅ Files uploaded:", data);

      // Return array of uploaded file URLs
      return data.file_urls || [];
    } catch (error) {
      console.error("❌ File upload error:", error);
      alert("Failed to upload files. Please try again.");
      return [];
    } finally {
      setUploadingFiles(false);
    }
  };

  // ✅ Send message with files
  const handleSend = async () => {
    if (!comment.trim() && selectedFiles.length === 0) {
      console.log("⚠️ Empty message and no files");
      return;
    }

    if (!roomKey || !studentId) {
      console.error("❌ Cannot send - missing room or student");
      return;
    }

    if (connectionState.status !== "connected") {
      console.error("❌ Cannot send - not connected");
      alert("Not connected. Please wait...");
      return;
    }

    const cid = genId();

    // ✅ Upload files first if any
    let fileUrls = [];
    if (selectedFiles.length > 0) {
      fileUrls = await uploadFiles(selectedFiles);

      if (fileUrls.length === 0 && selectedFiles.length > 0) {
        // Upload failed
        return;
      }
    }

    console.log("📤 Preparing to send message:", {
      client_message_id: cid,
      roomname,
      message: comment.substring(0, 50) + "...",
      files: fileUrls.length,
    });

    // ✅ Create optimistic message
    const optimistic = {
      client_message_id: cid,
      message: comment,
      sender_id: userId,
      sender_username: username,
      first_name: firstName,
      last_name: lastName,
      sender_role: userRole,
      sender_profile_pic: userPic,
      file_urls: fileUrls,
      date_sent: new Date().toISOString(),
      isPending: true,
      receiver_id: studentId,
    };

    // ✅ Add optimistic message to UI
    setMessages((prev) =>
      [optimistic, ...prev].sort(
        (a, b) => new Date(b.date_sent) - new Date(a.date_sent)
      )
    );

    setPending((p) => ({ ...p, [cid]: optimistic }));

    // ✅ Send via WebSocket
    sendMessage(roomKey, studentId, {
      message: comment,
      sender_id: userId,
      receiver_id: studentId,
      client_message_id: cid,
      file_urls: fileUrls,
    });

    // ✅ Clear input and files
    setComment("");
    if (userId && roomname) {
      localStorage.removeItem(`chat_draft_${userId}_${roomname}`);
    }
    setSelectedFiles([]);

    console.log("✅ Message sent, awaiting server confirmation");
  };

  // const handleKeyDown = (e) => {
  //   if (e.key === "Enter" && !e.shiftKey) {
  //     e.preventDefault();
  //     handleSend();
  //   }
  // };

  const groupByDate = (msgs) => {
    const grouped = {};
    msgs.forEach((m) => {
      const date = new Date(m.date_sent).toDateString();
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(m);
    });
    return grouped;
  };

  const grouped = groupByDate(messages);

  // ✅ Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  // ✅ Get file icon
  // const getFileIcon = (fileName) => {
  //   if (!fileName) return <IconFile size={16} className="flex-shrink-0" />;

  //   const ext = fileName.split(".").pop().toLowerCase();
  //   if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext))
  //     return <IconPhoto size={16} className="flex-shrink-0" />;
  //   return <IconFile size={16} className="flex-shrink-0" />;
  // };
  const getFileIcon = (fileName) => {
    const ext = (fileName?.split(".").pop() || "").toLowerCase();

    // Images
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
      return <IconPhoto size={16} className="flex-shrink-0" />;
    }

    // PDF
    if (ext === "pdf") {
      return (
        <IconFileTypePdf size={16} className="flex-shrink-0 text-red-600" />
      );
    }

    // Word
    if (["doc", "docx"].includes(ext)) {
      return (
        <IconFileTypeDocx size={16} className="flex-shrink-0 text-blue-600" />
      );
    }

    // Excel
    if (["xls", "xlsx"].includes(ext)) {
      return (
        <IconFileTypeXls size={16} className="flex-shrink-0 text-green-600" />
      );
    }

    // CSV
    if (ext === "csv") {
      return (
        <IconFileTypeCsv size={16} className="flex-shrink-0 text-green-700" />
      );
    }

    // PPT
    if (["ppt", "pptx"].includes(ext)) {
      return (
        <IconFileTypePpt size={16} className="flex-shrink-0 text-orange-600" />
      );
    }

    // TXT
    if (ext === "txt") {
      return (
        <IconFileTypeTxt size={16} className="flex-shrink-0 text-gray-600" />
      );
    }

    // Default
    return <IconFile size={16} className="flex-shrink-0" />;
  };

  // Decode HTML entities so pasted/escaped HTML (e.g. &lt;a href=...&gt;) renders as links
  const decodeHtmlEntities = (str) => {
    if (!str || typeof str !== "string") return str;
    return str
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&amp;/g, "&");
  };

  // Check if string already contains HTML tags (e.g. from Quill or pasted content)
  const containsHtml = (str) => /<[a-z][\s\S]*>/i.test(str);

  const linkifyHtml = (htmlString, isCurrentUser) => {
    if (!htmlString) return "";
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, "text/html");
      
      const textNodes = [];
      const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          let parent = node.parentNode;
          let insideLink = false;
          while (parent) {
            if (parent.tagName === "A") {
              insideLink = true;
              break;
            }
            parent = parent.parentNode;
          }
          if (!insideLink && node.nodeValue.trim()) {
            textNodes.push(node);
          }
        } else {
          for (let i = 0; i < node.childNodes.length; i++) {
            walk(node.childNodes[i]);
          }
        }
      };
      
      walk(doc.body);
      
      const urlRegex = /\b((https?:\/\/|www\.)[^\s<]+[^<.,:;"')\]\s])/gi;
      
      for (const node of textNodes) {
        const text = node.nodeValue;
        if (urlRegex.test(text)) {
          urlRegex.lastIndex = 0;
          const parent = node.parentNode;
          const span = doc.createElement("span");
          
          let lastIndex = 0;
          let match;
          
          while ((match = urlRegex.exec(text)) !== null) {
            const matchText = match[0];
            const matchIndex = match.index;
            
            if (matchIndex > lastIndex) {
              span.appendChild(doc.createTextNode(text.substring(lastIndex, matchIndex)));
            }
            
            const url = matchText.startsWith("http") ? matchText : `https://${matchText}`;
            const a = doc.createElement("a");
            a.href = url;
            a.target = "_blank";
            a.rel = "noopener noreferrer";
            a.textContent = matchText;
            
            const linkColorClass = isCurrentUser
              ? "text-blue-900 underline font-semibold cursor-pointer hover:text-blue-800 transition-colors"
              : "text-blue-600 underline font-semibold hover:text-blue-800 cursor-pointer transition-colors";
              
            a.className = `${linkColorClass} break-words`;
            span.appendChild(a);
            
            lastIndex = urlRegex.lastIndex;
          }
          
          if (lastIndex < text.length) {
            span.appendChild(doc.createTextNode(text.substring(lastIndex)));
          }
          
          parent.replaceChild(span, node);
        }
      }
      
      return doc.body.innerHTML;
    } catch (e) {
      console.error("Error linkifying HTML:", e);
      return htmlString;
    }
  };

  // Prepare message for display: decode entities, then sanitize (and linkify only if plain text)
  const prepareMessageHtml = (message, isCurrentUser) => {
    if (!message) return "";
    const decoded = decodeHtmlEntities(message);
    const htmlWithLinks = linkifyHtml(decoded, isCurrentUser);
    
    // Add support for newlines in plain text
    const processedHtml = containsHtml(decoded) ? htmlWithLinks : htmlWithLinks.replace(/\n/g, "<br/>");
    
    return DOMPurify.sanitize(processedHtml, {
      ALLOWED_TAGS: ["a", "b", "i", "u", "strong", "em", "br", "p", "ul", "ol", "li", "span"],
      ALLOWED_ATTR: ["href", "target", "rel", "class"],
    });
  };

  // ✅ Avatar generation functions
  const getInitialsAvatar = (firstName, lastName, username) => {
    try {
      if (
        firstName &&
        firstName.length > 0 &&
        lastName &&
        lastName.length > 0
      ) {
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
      }

      if (firstName && firstName.length > 0) {
        return `${firstName.charAt(0)}${
          firstName.length > 1 ? firstName.charAt(1) : ""
        }`.toUpperCase();
      }

      if (lastName && lastName.length > 0) {
        return `${lastName.charAt(0)}${
          lastName.length > 1 ? lastName.charAt(1) : ""
        }`.toUpperCase();
      }

      if (username && username.length > 0) {
        const parts = username.split(/[\s_.-]/);
        let path = "/" + parts.join("/");
        const fullUrl = `${process.env.REACT_APP_CURRENT_URL}${path}`;
        if (parts.length > 1) {
          return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(
            0
          )}`.toUpperCase();
        }
        if (username.length > 1) {
          return `${username.charAt(0)}${username.charAt(
            username.length - 1
          )}`.toUpperCase();
        }
        if (username.length === 1) {
          return `${username.charAt(0)}A`.toUpperCase();
        }
      }

      return "AD";
    } catch (error) {
      console.error("Error generating initials:", error);
      return "AD";
    }
  };

  // ✅ ALWAYS serve downloads from /backend/media
  const buildBackendMediaUrl = (fileUrl) => {
    if (!fileUrl) return null;

    const base = process.env.REACT_APP_BASE_URL?.replace(/\/$/, "") || "";

    // Absolute URL
    if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
      try {
        const u = new URL(fileUrl);

        // already correct
        if (u.pathname.startsWith("/backend/media/")) return u.toString();

        // convert /media/...  -> /backend/media/...
        if (u.pathname.startsWith("/media/")) {
          u.pathname = "/backend" + u.pathname; // => /backend/media/...
          return u.toString();
        }

        // leave other absolute paths as-is
        return fileUrl;
      } catch {
        return fileUrl;
      }
    }

    // Relative path
    let p = fileUrl.replace(/\\/g, "/").replace(/%5C/g, "/");

    if (p.startsWith("/backend/media/")) return `${base}${p}`;
    if (p.startsWith("/media/")) return `${base}/backend${p}`; // ✅ add /backend
    if (p.startsWith("media/")) return `${base}/backend/${p}`;

    // fallback: treat as media-relative
    p = p.replace(/^\/+/, "");
    return `${base}/backend/media/${p}`;
  };

  const getAvatarColor = (initials) => {
    try {
      if (!initials || initials.length === 0) return "#757575";

      const hash = initials
        .split("")
        .reduce((acc, char) => acc + char.charCodeAt(0), 0);

      const colors = [
        "#1E88E5",
        "#43A047",
        "#E53935",
        "#FB8C00",
        "#8E24AA",
        "#00ACC1",
        "#3949AB",
        "#00897B",
        "#7CB342",
        "#C0CA33",
        "#FFB300",
        "#F4511E",
        "#6D4C41",
        "#757575",
        "#546E7A",
      ];

      return colors[hash % colors.length];
    } catch (error) {
      console.error("Error getting avatar color:", error);
      return "#757575";
    }
  };

  const generateAvatar = (
    firstName,
    lastName,
    username,
    profilePic,
    size = "small"
  ) => {
    try {
      const sizeClass =
        size === "small"
          ? "w-7 h-7 text-xs sm:w-8 sm:h-8 sm:text-sm"
          : "w-9 h-9 text-sm sm:w-10 sm:h-10 sm:text-base";

      if (profilePic) {
        return (
          <div
            className={`${sizeClass} rounded-full overflow-hidden border-2 border-white shadow-sm flex-shrink-0`}
          >
            <img
              src={profilePic}
              alt={`${firstName || username || "User"}'s avatar`}
              className="w-full h-full object-cover"
              onError={(e) => {
                const initials = getInitialsAvatar(
                  firstName,
                  lastName,
                  username
                );
                e.target.style.display = "none";
                e.target.parentNode.innerHTML = `
                  <div class="flex items-center justify-center rounded-full ${sizeClass} text-white font-medium"
                    style="background-color: ${getAvatarColor(initials)}">
                    ${initials}
                  </div>
                `;
              }}
            />
          </div>
        );
      }

      const initials = getInitialsAvatar(firstName, lastName, username);
      const backgroundColor = getAvatarColor(initials);

      return (
        <div
          className={`flex items-center justify-center rounded-full ${sizeClass} text-white font-medium flex-shrink-0`}
          style={{ backgroundColor }}
        >
          {initials}
        </div>
      );
    } catch (error) {
      console.error("Error generating avatar:", error);
      return (
        <div className="flex items-center justify-center rounded-full w-7 h-7 sm:w-8 sm:h-8 text-white font-medium text-xs sm:text-sm flex-shrink-0 bg-gray-500">
          AD
        </div>
      );
    }
  };

  // ✅ Extract clean filename
  const extractFileName = (fileName) => {
    if (!fileName) return "file";

    try {
      const decodedName = decodeURIComponent(fileName);
      return decodedName.replace(/^[a-f0-9]{32,}_/, "");
    } catch (error) {
      console.error("Error extracting filename:", error);
      return fileName;
    }
  };

  const getNormalizedBackendPath = (fileUrl) => {
    if (!fileUrl) return null;

    // If already absolute, just return it
    if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
      return fileUrl;
    }

    // If backend sends /media/... make it absolute
    if (fileUrl.startsWith("/media/")) {
      return `${process.env.REACT_APP_BASE_URL}/backend${fileUrl}`;
    }

    // If backend sends /backend/media/... make it absolute
    if (fileUrl.startsWith("/backend/media/")) {
      return `${process.env.REACT_APP_BASE_URL}${fileUrl}`;
    }

    // If backend sends media/... make it absolute
    if (fileUrl.startsWith("media/")) {
      return `${process.env.REACT_APP_BASE_URL}/backend/${fileUrl}`;
    }

    // fallback
    return `${process.env.REACT_APP_BASE_URL}/backend/media/${fileUrl.replace(
      /^\/+/,
      ""
    )}`;
  };

  // ✅ Normalize file path
  const normalizeFilePath = (path) => {
    if (!path) return null;

    try {
      let normalizedPath = path.replace(/\\/g, "/").replace(/%5C/g, "/");

      if (
        normalizedPath.startsWith("http://") ||
        normalizedPath.startsWith("https://")
      ) {
        return normalizedPath;
      }

      if (normalizedPath.startsWith("/media")) {
        return `${process.env.REACT_APP_BASE_URL}${normalizedPath}`;
      }

      if (!normalizedPath.startsWith("/")) {
        return `${process.env.REACT_APP_BASE_URL}/media/${normalizedPath}`;
      }

      return normalizedPath;
    } catch (error) {
      console.error("Error normalizing path:", error);
      return path;
    }
  };

  const forceDownload = (url, filename) => {
    if (!url) {
      console.error("No URL provided for download");
      return;
    }

    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error forcing download:", error);
      window.open(url, "_blank");
    }
  };

  const toHttps = (url = "") => {
    try {
      const u = new URL(url);
      u.protocol = "https:";
      return u.toString().replace(/\/$/, ""); // remove trailing slash
    } catch {
      // if it's not a valid absolute URL, just return as-is
      return url;
    }
  };

  const BASE_HTTPS = toHttps(process.env.REACT_APP_BASE_URL);

  return (
    <>
      <div className="border-t p-2 sm:p-3 md:p-4 bg-gray-50 flex-shrink-0 w-full max-w-full">
        {/* ✅ File Preview Section */}
        {selectedFiles.length > 0 && (
          <div className="mb-2 sm:mb-3 flex flex-wrap gap-2 overflow-x-auto max-w-full">
            {selectedFiles.map((fileObj) => (
              <div
                key={fileObj.id}
                className="relative bg-white border border-gray-300 rounded-lg p-1.5 sm:p-2 flex items-center gap-1.5 sm:gap-2 max-w-[180px] sm:max-w-xs shadow-sm flex-shrink-0"
              >
                {/* Image Preview */}
                {fileObj.preview ? (
                  <img
                    src={fileObj.preview}
                    alt={fileObj.name}
                    className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                    {getFileIcon(fileObj.name)}
                  </div>
                )}

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium truncate">
                    {fileObj.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(fileObj.size)}
                  </p>
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => removeFile(fileObj.id)}
                  className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-red-500 text-white rounded-full p-0.5 sm:p-1 hover:bg-red-600 transition shadow-md"
                >
                  <IconX size={12} className="sm:w-3.5 sm:h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ✅ Quill Editor with Responsive Styling */}
        <div className="w-full max-w-full mb-2">
          <ReactQuill
            value={comment}
            onChange={handleCommentChange}
            ref={quillRef}
            placeholder="Type your message..."
            // onKeyDown={handleKeyDown}
            className="responsive-quill"
            modules={{
              toolbar: [
                ["bold", "italic", "underline"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["link"],
                ["clean"],
              ],
            }}
          />
        </div>

        <div className="flex justify-between items-center gap-2 w-full max-w-full">
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            multiple
            accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.csv,.ppt,.pptx"
            onChange={handleFileSelect}
          />

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingFiles}
            className="p-1.5 sm:p-2 rounded hover:bg-gray-200 transition disabled:opacity-50 flex items-center gap-1 flex-shrink-0"
            title="Attach files"
          >
            <IconPaperclip size={18} className="sm:w-5 sm:h-5" />
          </button>

          <button
            onClick={handleSend}
            disabled={
              (!comment.trim() && selectedFiles.length === 0) ||
              connectionState.status !== "connected" ||
              uploadingFiles
            }
            className="px-3 py-1.5 sm:px-4 sm:py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-medium flex-shrink-0"
          >
            {uploadingFiles ? (
              <>
                <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span className="hidden xs:inline">Uploading...</span>
              </>
            ) : (
              <>
                <IconSend size={16} className="sm:w-4 sm:h-4 flex-shrink-0" />
                <span>Send</span>
              </>
            )}
          </button>
        </div>
      </div>
      {/* ✅ LOADING OVERLAY */}
      {uploadingFiles && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 sm:p-8 shadow-2xl flex flex-col items-center max-w-sm w-full mx-4">
            <svg
              className="animate-spin h-12 w-12 sm:h-16 sm:w-16 text-purple-600 mb-4"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <p className="text-lg sm:text-xl font-semibold text-gray-800 mb-2 text-center">
              Uploading files...
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mt-2 sm:mt-4 text-center">
              Please wait, do not close or refresh the page
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col h-full w-full max-w-full bg-white rounded shadow overflow-hidden">
        {/* ✅ CONNECTION STATUS */}
        {connectionState.status !== "connected" && (
          <div
            className={`p-2 text-center text-xs font-medium flex-shrink-0 ${
              connectionState.status === "connecting"
                ? "bg-yellow-100 text-yellow-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {connectionState.status === "connecting"
              ? "Connecting..."
              : "Disconnected - Reconnecting..."}
          </div>
        )}

        {/* ✅ Messages Container - Now on top */}
        <div
          className="flex-1 overflow-y-auto p-2 sm:p-3 md:p-4 w-full max-w-full"
          ref={messagesContainerRef}
        >
          <div ref={topRef} />
          {loadingHistory ? (
            <div className="text-center text-gray-500 mt-10 text-sm">
              Loading messages...
            </div>
          ) : Object.keys(grouped).length === 0 ? (
            <div className="text-center text-gray-400 mt-10 text-sm px-4">
              No messages yet. Start the conversation!
            </div>
          ) : (
            Object.entries(grouped).map(([date, msgs]) => (
              <div key={date} className="w-full max-w-full">
                {/* ✅ DATE DIVIDER */}
                <div className="flex justify-center my-3">
                  <div className="px-3 py-1 rounded-full bg-gray-200 text-xs text-gray-600">
                    {new Date(date).toLocaleDateString("en-US", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </div>

                {/* ✅ MESSAGES */}
                {msgs.map((m) => {
                  const mine = m.sender_id === userId;
                  const isPending = pending[m.client_message_id];
                  const isFailed = m.isFailed;
                  const hasMessage = m.message && m.message.trim();
                  const hasFiles = m.file_urls && m.file_urls.length > 0;

                  return (
                    <div
                      key={m.message_id || m.client_message_id}
                      className={`flex my-2 sm:my-3 gap-1.5 sm:gap-2 w-full max-w-full ${
                        mine ? "justify-end" : "justify-start"
                      }`}
                    >
                      {/* ✅ AVATAR (for other users) */}
                      {!mine && (
                        <div className="flex-shrink-0">
                          {generateAvatar(
                            m.first_name,
                            m.last_name,
                            m.sender_username || "User",
                            m.sender_profile_pic
                          )}
                        </div>
                      )}

                      {/* 🎨 MESSAGE BUBBLE WITH RESPONSIVE SIZING */}
                      <div
                        className={`p-2 sm:p-3 rounded-lg shadow-sm min-w-0 ${
                          mine
                            ? "bg-emerald-50 text-gray-900 rounded-tr-none border border-emerald-200 max-w-[75%] sm:max-w-xs md:max-w-md"
                            : "bg-white text-gray-900 rounded-tl-none border border-gray-200 max-w-[75%] sm:max-w-xs md:max-w-md"
                        } ${isPending ? "opacity-60" : ""}`}
                      >
                        {/* ✅ SENDER NAME (for other users) */}
                        {!mine && (
                          <div className="text-xs font-semibold mb-1 text-gray-600 truncate">
                            {m.sender_username || "User"}
                          </div>
                        )}

                        {/* ✅ Message Text (if exists) */}
                        {hasMessage && (
                          <div className="ql-snow">
                            <div
                              className="text-xs sm:text-sm break-words prose prose-sm max-w-none leading-relaxed overflow-hidden ql-editor"
                              dangerouslySetInnerHTML={{
                                __html: prepareMessageHtml(m.message, mine),
                              }}
                            />
                          </div>
                        )}

                        {/* ✅ File Attachments */}
                        {/* {hasFiles && (
                          <div
                            className={`${hasMessage ? "mt-2" : ""} space-y-2`}
                          >
                            {m.file_urls.map((file, idx) => {
                              // Support both old string URLs and new structured file objects
                              const url =
                                typeof file === "string" ? file : file.url;

                              const fileName =
                                typeof file === "object"
                                  ? file.original_name || file.stored_name
                                  : decodeURIComponent(url.split("/").pop());

                              const isImage =
                                /\.(jpg|jpeg|png|gif|webp)$/i.test(url);

                              return isImage ? (
                                <img
                                  key={idx}
                                  src={url}
                                  alt={fileName}
                                  className="rounded max-w-full cursor-pointer hover:opacity-90 border border-gray-300 shadow-sm"
                                  onClick={() => window.open(url, "_blank")}
                                />
                              ) : (
                                <a
                                  key={idx}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`flex items-center gap-2 p-2 rounded min-w-0 ${
                                    mine
                                      ? "bg-emerald-200 text-emerald-900 hover:bg-emerald-300"
                                      : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                                  } transition shadow-sm`}
                                >
                                  {getFileIcon(fileName)}
                                  <span className="text-xs truncate font-medium min-w-0">
                                    {fileName}
                                  </span>
                                </a>
                              );
                            })}
                          </div>
                        )} */}
                        {hasFiles && (
                          <div
                            className={`${hasMessage ? "mt-2" : ""} space-y-2`}
                          >
                            {m.file_urls.map((file, idx) => {
                              // Support both old string URLs and new structured file objects
                              const fileUrl =
                                typeof file === "string" ? file : file.url;

                              const rawFileName = fileUrl.split("/").pop();
                              const cleanedFileName =
                                typeof file === "object"
                                  ? file.original_name || file.stored_name
                                  : extractFileName(rawFileName);

                              // Build proper path
                              const parts = fileUrl.split("/").slice(3);
                              let path = "/" + parts.join("/");
                              // path = path.replace(/\/backend/g, "");
                              path = path.replace(
                                /\/backend\/backend/g,
                                "/backend"
                              );
                              // const fullUrl = `${process.env.REACT_APP_BASE_URL}${path}`;
                              let fullUrl = buildBackendMediaUrl(fileUrl);
                              fullUrl = toHttps(fullUrl);

                              const isImage =
                                /\.(jpe?g|png|gif|bmp|webp)$/i.test(
                                  cleanedFileName
                                );
                              console.log("baseUrl", BASE_HTTPS);
                              console.log("path", path);
                              if (!fullUrl) return null;

                              return isImage ? (
                                <a
                                  key={idx}
                                  href={fullUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block"
                                >
                                  <img
                                    src={fullUrl}
                                    alt={cleanedFileName}
                                    className="rounded max-w-full cursor-pointer hover:opacity-90 border border-gray-300 shadow-sm"
                                    onError={(e) => {
                                      console.error(
                                        "Image load error:",
                                        e.target.src
                                      );
                                      e.target.style.display = "none";
                                    }}
                                  />
                                </a>
                              ) : (
                                <a
                                  key={idx}
                                  href={fullUrl}
                                  className={`flex items-center gap-2 p-2 rounded min-w-0 w-full ${
                                    mine
                                      ? "bg-emerald-200 text-emerald-900 hover:bg-emerald-300"
                                      : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                                  } transition shadow-sm`}
                                >
                                  {getFileIcon(cleanedFileName)}
                                  <span className="text-xs truncate font-medium min-w-0">
                                    {cleanedFileName}
                                  </span>
                                </a>
                              );
                            })}
                          </div>
                        )}

                        {/* ✅ Status Indicators */}
                        <div className="flex items-center justify-between mt-1 gap-2">
                          <span className="text-xs text-gray-500">
                            {new Date(m.date_sent).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>

                          {isPending && (
                            <span className="text-xs text-gray-500 italic">
                              Sending...
                            </span>
                          )}

                          {isFailed && (
                            <span className="text-xs text-red-600 font-medium italic">
                              ❌ Failed
                            </span>
                          )}
                        </div>
                      </div>

                      {/* ✅ AVATAR (for current user) */}
                      {mine && (
                        <div className="flex-shrink-0">
                          {generateAvatar(
                            firstName,
                            lastName,
                            username,
                            userPic
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* ✅ Input Area - Now at bottom */}
      </div>

      <style jsx>{`
        /* Responsive Quill Editor */
        .responsive-quill :global(.ql-container) {
          font-size: 13px;
          min-height: 60px;
        }

        @media (min-width: 640px) {
          .responsive-quill :global(.ql-container) {
            font-size: 14px;
            min-height: 80px;
          }
        }

        .responsive-quill :global(.ql-toolbar) {
          border-top-left-radius: 0.5rem;
          border-top-right-radius: 0.5rem;
        }

        .responsive-quill :global(.ql-container) {
          border-bottom-left-radius: 0.5rem;
          border-bottom-right-radius: 0.5rem;
        }

        /* Make toolbar buttons smaller on mobile */
        @media (max-width: 639px) {
          .responsive-quill :global(.ql-toolbar button) {
            width: 24px;
            height: 24px;
          }

          .responsive-quill :global(.ql-toolbar .ql-stroke) {
            stroke-width: 2;
          }
        }

        /* Ensure prose content doesn't overflow */
        .prose {
          overflow-wrap: break-word;
          word-wrap: break-word;
          word-break: break-word;
        }

        .prose * {
          max-width: 100%;
        }

        .prose img {
          height: auto;
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        ::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </>
  );
}
